import { httpClient } from "src/shared/services/httpClient";
import { AxiosPromise, AxiosResponse } from "axios";
import {
  CompanyInfo,
  ComplaintDataForNewViolationCase,
  DashboardParametricsResponse,
  DashboardResultsResponse,
  DashboardSectionEnum,
  DetailsView,
  FineParametersResponse,
  PaymentDetailsParams,
  SampleCompanyCaseDataForNewViolationCase,
  SupervisoryTeamViolationResponse,
  UpdateCorrectionData,
  ViolationParametrics,
} from "src/violationCase/types";

export const getFineParametrics = (
  companyIdParent?: string | null,
  violationTypeId?: string | null,
  financialYear?: number | null,
): Promise<AxiosResponse<FineParametersResponse>> =>
  httpClient.get(`/violationCase/fineParametrics`, {
    params: {
      companyId: companyIdParent,
      violationTypeId: violationTypeId,
      financialYear: financialYear,
    },
  });

export const postFineCalculation = (fields: {
  violationTypeCalculator: number | null;
  companyEntityTypeCalculator: number | null;
  legalTypeCalculator: number | null;
  companyStatusCalculator: number | null;
  violationRepeatTypeCalculator: number | null;
  companySizeTypeCalculator: number | null;
}): Promise<AxiosResponse<{ amount: number }>> => {
  const formData = {
    violationTypeCalculator: fields.violationTypeCalculator,
    companyEntityTypeCalculator: fields.companyEntityTypeCalculator,
    companyStatusCalculator: fields.companyStatusCalculator,
    legalTypeCalculator: fields.legalTypeCalculator,
    violationRepeatTypeCalculator: fields.violationRepeatTypeCalculator,
    companySizeTypeCalculator: fields.companySizeTypeCalculator,
  };
  return httpClient.post("/violationCase/fineCalculation", formData);
};

export const getViolationCaseDetails = (
  caseId: string | undefined,
  isChamber: boolean,
  isSystemAdmin: boolean,
  isGGEAdmin: boolean,
): Promise<AxiosResponse<DetailsView>> =>
  httpClient.get(
    `/violationCase/${isChamber || isSystemAdmin || isGGEAdmin ? "chamberDetails" : "companyDetails"}?violationCaseId=${caseId}`,
  );

export const getCorrectionDetails = (caseId: string | undefined) =>
  httpClient.get(`/violationCase/correctionDetails?violationCaseId=${caseId}`);

export const getHistoryDetails = (caseId: string | undefined) =>
  httpClient.get(`/violationCase/historyDetails?violationCaseId=${caseId}`);

export const getDisputeDetails = (caseId: string | undefined) =>
  httpClient.get(`/violationCase/disputeDetails?violationCaseId=${caseId}`);

export const updateUserComments = ({
  violationCaseId,
  notes,
}: {
  violationCaseId: string;
  notes: string;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("chamberUserNotes", notes);

  return httpClient.post("violationCase/chamberUserNotes", formData);
};

export const getDashboardParametrics = ({
  isChamber,
  isBusiness,
  isSupervisorAssignee,
  mode,
}: {
  isChamber: boolean;
  isBusiness: boolean;
  isSupervisorAssignee?: boolean;
  mode?: number;
}): AxiosPromise<DashboardParametricsResponse> => {
  if (
    isSupervisorAssignee &&
    mode === DashboardSectionEnum.SUPERVISORY_TEAM_ASSIGNEE
  ) {
    return httpClient.get(
      "supervisoryTeam/getViolationParametricsForSupervisoryTeam",
    );
  }

  const endpoint =
    isChamber && mode !== DashboardSectionEnum.SAMPLE_INITIATOR
      ? "getChamberDashboardParametrics"
      : isBusiness
        ? "getCompanyDashboardParametrics"
        : "getAdminDashboardParametrics";

  return httpClient.get(`/violationCase/${endpoint}`, {
    params: {
      ...(mode !== undefined && { mode }),
    },
  });
};

export const getChamberDashboardResults = (
  length: number,
  start: number,
  sortingData: string,
  fetchForEntireChamber: boolean,
  violationTypeId?: number[],
  violationSourceId?: number[],
  violationCaseStatusId?: number[],
  assigneeChamberUserId?: number[],
  violationCaseId?: number,
  companyId?: number,
  mode?: number,
  assigneeChamberAdminId?: number[],
  dateSubmittedFrom?: string,
  dateSubmittedTo?: string,
  fineDateSubmittedFrom?: string,
  fineDateSubmittedTo?: string,
  corporateStatusId?: number[],
): Promise<AxiosResponse<DashboardResultsResponse>> => {
  const formData = new FormData();
  formData.append("length", length.toString());
  formData.append("start", start.toString());
  formData.append("sortingData", sortingData);
  formData.append("fetchForEntireChamber", fetchForEntireChamber.toString());
  violationTypeId?.forEach((id) => {
    formData.append("violationTypeId", id.toString());
  });
  assigneeChamberAdminId?.forEach((id) => {
    formData.append("assigneeChamberAdminId", id.toString());
  });
  violationSourceId?.forEach((id) => {
    formData.append("violationSourceId", id.toString());
  });
  violationCaseStatusId?.forEach((id) => {
    formData.append("violationCaseStatusId", id.toString());
  });
  assigneeChamberUserId?.forEach((id) => {
    formData.append("assigneeChamberUserId", id.toString());
  });
  corporateStatusId?.forEach((id) => {
    formData.append("corporateStatusId", id.toString());
  });
  if (violationCaseId) {
    formData.append("violationCaseId", violationCaseId.toString());
  }
  if (companyId) {
    formData.append("companyId", companyId.toString());
  }
  if (mode) {
    formData.append("mode", mode.toString());
  }
  if (dateSubmittedFrom) {
    formData.append("dateSubmittedFrom", dateSubmittedFrom);
  }
  if (dateSubmittedTo) {
    formData.append("dateSubmittedTo", dateSubmittedTo);
  }
  if (fineDateSubmittedFrom) {
    formData.append("fineDateSubmittedFrom", fineDateSubmittedFrom);
  }
  if (fineDateSubmittedTo) {
    formData.append("fineDateSubmittedTo", fineDateSubmittedTo);
  }
  return httpClient.post("/violationCase/getChamberDashboardResults", formData);
};

export const createCorrection = (
  violationCaseId: string,
  companyReqText: string,
  correctionFile?: File,
): Promise<AxiosResponse<{ correctionId: number }>> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("companyText", companyReqText);
  if (correctionFile) {
    formData.append("companyCorrectionFile", correctionFile);
  }
  return httpClient.post("/violationCase/createCorrection", formData);
};

export const submitViolation = ({
  companyId,
  coName,
  companyAddress,
  companyEmail,
  afm,
  companyPhone,
  violationType,
  violationSourceType,
  violationDescription,
  chamberComplianceFile,
  calculatedFine,
  companyEntityTypeCalculator,
  companySizeTypeCalculator,
  companyStatusCalculator,
  legalTypeCalculator,
  violationRepeatTypeCalculator,
  violationTypeCalculator,
  complaintId,
  sampleCompanyCaseId,
  violationYear,
  thirdPartyFile,
}: {
  companyId: string;
  coName: string;
  companyAddress: string;
  companyEmail: string;
  afm: string;
  companyPhone: string;
  violationType: number;
  violationSourceType: number;
  violationDescription: string;
  chamberComplianceFile?: any;
  calculatedFine: number;
  companyEntityTypeCalculator: number;
  companySizeTypeCalculator: number;
  companyStatusCalculator: number;
  legalTypeCalculator: number;
  violationRepeatTypeCalculator: number;
  violationTypeCalculator: number;
  complaintId: string | undefined;
  sampleCompanyCaseId: string | undefined;
  violationYear: number | undefined;
  thirdPartyFile?: any;
}) => {
  const formData = new FormData();

  formData.append("companyId", companyId);
  formData.append("coName", coName);
  formData.append("companyAddress", companyAddress);
  formData.append("companyEmail", companyEmail);
  formData.append("afm", afm);
  formData.append("companyPhone", companyPhone);
  formData.append("violationTypeId", violationType.toString());
  formData.append("violationSourceTypeId", violationSourceType.toString());
  formData.append("violationDescription", violationDescription);

  if (chamberComplianceFile && chamberComplianceFile[0]) {
    formData.append("chamberComplianceFile", chamberComplianceFile[0]);
  }

  formData.append("calculatedFine", calculatedFine.toString());
  formData.append(
    "companyEntityTypeCalculator",
    companyEntityTypeCalculator.toString(),
  );
  if (companySizeTypeCalculator) {
    formData.append(
      "companySizeTypeCalculator",
      companySizeTypeCalculator.toString(),
    );
  }
  formData.append(
    "companyStatusCalculator",
    companyStatusCalculator.toString(),
  );
  formData.append("legalTypeCalculator", legalTypeCalculator.toString());
  formData.append(
    "violationRepeatTypeCalculator",
    violationRepeatTypeCalculator.toString(),
  );
  formData.append(
    "violationTypeCalculator",
    violationTypeCalculator.toString(),
  );

  if (complaintId) {
    formData.append("complaintId", complaintId);
  }

  if (sampleCompanyCaseId) {
    formData.append("sampleCompanyCaseId", sampleCompanyCaseId);
  }

  if (violationYear) {
    formData.append("financialYear", violationYear.toString());
  }

  if (thirdPartyFile && thirdPartyFile[0]) {
    formData.append("thirdPartyFile", thirdPartyFile[0]);
  }

  return httpClient.post("violationCase/createViolationCase", formData);
};

export const getComplaineeInfo = ({
  gemiNumber,
}: {
  gemiNumber: number;
}): Promise<AxiosResponse<CompanyInfo>> =>
  httpClient.get(
    "violationCase/companyInfo?companyId=" + gemiNumber.toString(),
  );

export const getViolationParametrics = (): Promise<
  AxiosResponse<ViolationParametrics>
> => httpClient.get("/violationCase/violationParametrics");

export const assignCaseToUser = ({
  assigneeChamberAdmin,
  assigneeChamberUser,
  assigneeChamberAdminMessage,
  assigneeChamberUserMessage,
  caseId,
}: {
  assigneeChamberAdmin: string;
  assigneeChamberUser: string;
  assigneeChamberAdminMessage: string;
  assigneeChamberUserMessage: string;
  caseId: string;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", caseId);
  formData.append("assigneeChamberAdminId", assigneeChamberAdmin);
  formData.append("assigneeChamberUserId", assigneeChamberUser);
  formData.append("assigneeChamberUserMessage", assigneeChamberUserMessage);
  formData.append("assigneeChamberAdminMessage", assigneeChamberAdminMessage);
  return httpClient.post("/violationCase/assignCase", formData);
};

export const getChamberUsers = () =>
  httpClient.get(`violationCase/organizationUsers`);

export const getCompanyDashboardResults = (
  length: number,
  start: number,
  sortingData: string,
  fetchForEntireChamber: boolean,
  violationTypeId?: number[],
  violationCaseStatusId?: number[],
  violationCaseId?: number,
): Promise<AxiosResponse<DashboardResultsResponse>> => {
  const formData = new FormData();
  formData.append("length", length.toString());
  formData.append("start", start.toString());
  formData.append("sortingData", sortingData);
  formData.append("fetchForEntireChamber", fetchForEntireChamber.toString());

  violationTypeId?.forEach((id) => {
    formData.append("violationTypeId", id.toString());
  });
  violationCaseStatusId?.forEach((id) => {
    formData.append("violationCaseStatusId", id.toString());
  });
  if (violationCaseId) {
    formData.append("violationCaseId", violationCaseId.toString());
  }
  return httpClient.post<DashboardResultsResponse>(
    "violationCase/getCompanyDashboardResults",
    formData,
  );
};

export const getAdminDashboardResults = (
  length: number,
  start: number,
  sortingData: string,
  violationTypeId?: number[],
  organizationId?: number,
  violationCaseId?: number,
  violationCaseStatusId?: number[],
  companyId?: number,
  dateSubmittedFrom?: string,
  dateSubmittedTo?: string,
  fineDateSubmittedFrom?: string,
  fineDateSubmittedTo?: string,
  corporateStatusId?: number[],
): Promise<AxiosResponse<DashboardResultsResponse>> => {
  const formData = new FormData();
  formData.append("length", length.toString());
  formData.append("start", start.toString());
  formData.append("sortingData", sortingData);

  violationTypeId?.forEach((id) => {
    formData.append("violationTypeId", id.toString());
  });
  if (organizationId) {
    formData.append("organizationId", organizationId.toString());
  }
  if (violationCaseId) {
    formData.append("violationCaseId", violationCaseId.toString());
  }
  violationCaseStatusId?.forEach((id) => {
    formData.append("violationCaseStatusId", id.toString());
  });
  if (companyId) {
    formData.append("companyId", companyId.toString());
  }
  if (dateSubmittedFrom) {
    formData.append("dateSubmittedFrom", dateSubmittedFrom);
  }
  if (dateSubmittedTo) {
    formData.append("dateSubmittedTo", dateSubmittedTo);
  }
  if (fineDateSubmittedFrom) {
    formData.append("fineDateSubmittedFrom", fineDateSubmittedFrom);
  }
  if (fineDateSubmittedTo) {
    formData.append("fineDateSubmittedTo", fineDateSubmittedTo);
  }
  corporateStatusId?.forEach((id) => {
    formData.append("corporateStatusId", id.toString());
  });
  return httpClient.post<DashboardResultsResponse>(
    "violationCase/getAdminDashboardResults",
    formData,
  );
};

export const reviewCorrection = (
  violationCaseId: string,
  chamberReqText: string,
  correctionStatusId: string,
  correctionFile?: File,
  applicationIds?: number[],
): Promise<AxiosResponse<{ correctionId: number }>> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("chamberText", chamberReqText);
  formData.append("correctionStatusId", correctionStatusId);
  if (correctionFile) {
    formData.append("chamberCorrectionFile", correctionFile);
  }
  applicationIds?.forEach((id) => {
    formData.append("applicationIds", id.toString());
  });
  return httpClient.post("/violationCase/reviewCorrection", formData);
};

export const updateFine = ({
  violationCaseId,
  violationTypeCalculator,
  companyEntityTypeCalculator,
  legalTypeCalculator,
  companyStatusCalculator,
  violationRepeatTypeCalculator,
  companySizeTypeCalculator,
}: {
  violationCaseId: string | undefined;
  violationTypeCalculator: number;
  companyEntityTypeCalculator: number;
  legalTypeCalculator: number;
  companyStatusCalculator: number;
  violationRepeatTypeCalculator: number;
  companySizeTypeCalculator: number;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId ?? "");
  formData.append(
    "violationTypeCalculator",
    violationTypeCalculator.toString(),
  );
  formData.append(
    "companyEntityTypeCalculator",
    companyEntityTypeCalculator.toString(),
  );
  formData.append("legalTypeCalculator", legalTypeCalculator.toString());
  formData.append(
    "companyStatusCalculator",
    companyStatusCalculator.toString(),
  );
  formData.append(
    "violationRepeatTypeCalculator",
    violationRepeatTypeCalculator.toString(),
  );
  if (companySizeTypeCalculator) {
    formData.append(
      "companySizeTypeCalculator",
      companySizeTypeCalculator.toString(),
    );
  }

  return httpClient.post("/violationCase/updateFine", formData);
};

export const submitCompanyDispute = ({
  companyReqFile,
  companyReqText,
  disputeTypeId,
  violationCaseId,
}: {
  companyReqFile?: any;
  companyReqText: string;
  disputeTypeId: string;
  violationCaseId: string | undefined;
}): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("companyText", companyReqText.toString());
  formData.append("disputeTypeId", disputeTypeId.toString());
  // File is optional for non-IMPEDIMENT types; unguarded indexing sent the string "undefined".
  if (companyReqFile?.[0]) {
    formData.append("companyDisputeFile", companyReqFile[0]);
  }
  formData.append("violationCaseId", violationCaseId ?? "");
  return httpClient.post("/violationCase/createDispute", formData);
};

export const skipToHearing = ({
  violationCaseId,
}: {
  violationCaseId: string | undefined;
}): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId ?? "");
  return httpClient.post("/violationCase/skipToHearing", formData);
};

export const acceptViolationPayFull = ({
  violationCaseId,
}: {
  violationCaseId: string | undefined;
}): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId ?? "");
  return httpClient.post("/violationCase/acceptViolationPayFull", formData);
};

export const submitDelete = ({
  violationCaseId,
  deleteViolationCaseFile,
  deleteViolationCaseText,
  applicationIds,
}: {
  violationCaseId: string;
  deleteViolationCaseFile?: any;
  deleteViolationCaseText: string;
  applicationIds: number[] | undefined;
}): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("deleteViolationCaseText", deleteViolationCaseText);
  if (deleteViolationCaseFile) {
    formData.append("deleteViolationCaseFile", deleteViolationCaseFile);
  }
  applicationIds?.forEach((id) => {
    formData.append("applicationIds", id.toString());
  });
  return httpClient.post("/violationCase/deleteCase", formData);
};

export const evaluateDispute = (
  violationCaseId: string,
  chamberReqText: string,
  evaluateDisputeStatusId: string,
  evaluateDisputeFileName?: File,
): Promise<AxiosResponse<{ correctionId: number }>> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("chamberText", chamberReqText);
  formData.append("disputeStatusId", evaluateDisputeStatusId);
  if (evaluateDisputeFileName) {
    formData.append("chamberFile", evaluateDisputeFileName);
  }
  return httpClient.post("/violationCase/updateDispute", formData);
};

export const disputeProposal = (
  violationCaseId: string,
  chamberReqText: string,
): Promise<AxiosResponse<any>> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("chamberProposalText", chamberReqText);

  return httpClient.post("/violationCase/submitDisputeProposal", formData);
};

export const getComplaintDataForNewViolationCase = (
  complaintId: string | undefined,
): Promise<AxiosResponse<ComplaintDataForNewViolationCase>> =>
  httpClient.get(
    `/complaint/getComplaintDataForNewViolationCase?complaintId=${complaintId}`,
  );

export const getSampleCompanyCaseDataForNewViolationCase = (
  sampleCompanyCaseId: string | undefined,
): Promise<AxiosResponse<SampleCompanyCaseDataForNewViolationCase>> =>
  httpClient.get(
    `/sampleInspection/getSampleCompanyCaseDataForNewViolationCase?sampleCompanyCaseId=${sampleCompanyCaseId}`,
  );

export const finalizeCloseCase = ({
  violationCaseId,
  applicationIds,
  chamberText,
}: {
  violationCaseId: string | undefined;
  applicationIds?: number[] | undefined | null;
  chamberText: string;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("chamberText", chamberText);
  applicationIds?.forEach((id) => {
    formData.append("applicationIds", id.toString());
  });
  return httpClient.post("/violationCase/finalizeCloseCase", formData);
};

export const uploadFinePostFile = ({
  violationCaseId,
  evaluateDisputeFileName,
}: {
  violationCaseId: string;
  evaluateDisputeFileName: File;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("finePostFile", evaluateDisputeFileName);
  return httpClient.post("/violationCase/setFinePostFile", formData);
};

export const submitRating = ({
  violationCaseId,
  decisionOfCorrection,
  chamberResponseText,
  chamberResponseFile,
  applicationIds,
}: {
  violationCaseId: string;
  decisionOfCorrection: string;
  chamberResponseText: string;
  chamberResponseFile?: any;
  applicationIds?: number[] | undefined | null;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId);
  formData.append("decisionOfCorrection", decisionOfCorrection);
  formData.append("chamberResponseText", chamberResponseText);
  if (chamberResponseFile) {
    formData.append("chamberResponseFile", chamberResponseFile);
  }
  applicationIds?.forEach((id) => {
    formData.append("applicationIds", id.toString());
  });
  return httpClient.post("/violationCase/updateCorrection", formData);
};

export const getUpdateCorrectionData = ({
  violationCaseId,
}: {
  violationCaseId: string;
}): Promise<AxiosResponse<UpdateCorrectionData>> => {
  return httpClient.get(`violationCase/getUpdateCorrectionData`, {
    params: { violationCaseId },
  });
};

export const getPaymentDetailsParams = (
  caseId: string,
): Promise<AxiosResponse<PaymentDetailsParams>> =>
  httpClient.get(`/violationCase/paymentDetailsParams?caseId=${caseId}`);

export const submitCorrectionAndPayment = ({
  companyCorrectionFile,
  companyText,
  violationCaseId,
  applicationIds,
}: {
  companyCorrectionFile?: any;
  companyText: string;
  violationCaseId: string | undefined;
  applicationIds: [] | undefined;
}): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("companyText", companyText.toString());
  formData.append("companyCorrectionFile", companyCorrectionFile[0]);
  formData.append("violationCaseId", violationCaseId ?? "");
  applicationIds?.forEach((id) => {
    formData.append("applicationIds", id);
  });
  return httpClient.post("/violationCase/createCorrection", formData);
};

export const declarePaymentCompletedAndForwardToChamber = (
  violationCaseId: string | undefined,
): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId ?? "");
  return httpClient.post(
    "/violationCase/declarePaymentCompletedAndForwardToChamber",
    formData,
  );
};

export const expireNow = ({
  violationCaseId,
}: {
  violationCaseId: string | undefined;
}) => {
  const formData = new FormData();
  if (violationCaseId) {
    formData.append("violationCaseId", violationCaseId);
  }
  return httpClient.post("/violationCase/expireNow", formData);
};

export const updateDraftViolation = ({
  violationCaseId,
  violationTypeCalculator,
  companyEntityTypeCalculator,
  legalTypeCalculator,
  companyStatusCalculator,
  violationRepeatTypeCalculator,
  companySizeTypeCalculator,
  chamberComplianceInstruction,
}: {
  violationCaseId: string | undefined;
  violationTypeCalculator: number;
  companyEntityTypeCalculator: number;
  legalTypeCalculator: number;
  companyStatusCalculator: number;
  violationRepeatTypeCalculator: number;
  companySizeTypeCalculator: number | null;
  chamberComplianceInstruction: string;
}) => {
  const formData = new FormData();
  formData.append("violationCaseId", violationCaseId ?? "");
  formData.append(
    "violationTypeCalculator",
    violationTypeCalculator.toString(),
  );
  formData.append(
    "companyEntityTypeCalculator",
    companyEntityTypeCalculator.toString(),
  );
  formData.append("legalTypeCalculator", legalTypeCalculator.toString());
  formData.append(
    "companyStatusCalculator",
    companyStatusCalculator.toString(),
  );
  formData.append(
    "violationRepeatTypeCalculator",
    violationRepeatTypeCalculator.toString(),
  );
  if (companySizeTypeCalculator) {
    formData.append(
      "companySizeTypeCalculator",
      companySizeTypeCalculator.toString(),
    );
  }

  formData.append("chamberComplianceInstruction", chamberComplianceInstruction);

  return httpClient.post("/violationCase/updateDraftViolation", formData);
};

export const getViolationsForSupervisoryTeam = (
  length: number,
  start: number,
  sortingData: string,
  violationCaseId?: number,
  violationTypeId?: number[],
  violationCaseStatusId?: number[],
  companyId?: number,
  organization?: number,
  violationSourceId?: number[],
  dateSubmittedFrom?: string,
  dateSubmittedTo?: string,
  fineDateSubmittedFrom?: string,
  fineDateSubmittedTo?: string,
): Promise<AxiosResponse<SupervisoryTeamViolationResponse>> => {
  const formData = new FormData();
  formData.append("length", length.toString());
  formData.append("start", start.toString());
  formData.append("sortingData", sortingData);
  if (violationCaseId)
    formData.append("violationCaseId", violationCaseId.toString());
  violationTypeId?.forEach((id) =>
    formData.append("violationTypeId", id.toString()),
  );
  violationCaseStatusId?.forEach((id) =>
    formData.append("violationCaseStatusId", id.toString()),
  );
  violationSourceId?.forEach((id) =>
    formData.append("violationSourceId", id.toString()),
  );
  if (companyId) formData.append("companyId", companyId.toString());
  if (organization) formData.append("organization", organization.toString());
  if (dateSubmittedFrom) {
    formData.append("dateSubmittedFrom", dateSubmittedFrom);
  }
  if (dateSubmittedTo) {
    formData.append("dateSubmittedTo", dateSubmittedTo);
  }
  if (fineDateSubmittedFrom) {
    formData.append("fineDateSubmittedFrom", fineDateSubmittedFrom);
  }
  if (fineDateSubmittedTo) {
    formData.append("fineDateSubmittedTo", fineDateSubmittedTo);
  }
  return httpClient.post(
    "supervisoryTeam/getViolationsForSupervisoryTeam",
    formData,
  );
};
